/**
 * Privacy Policy Page
 *
 * Comprehensive privacy policy including cookie and analytics disclosure
 */

import React, { useState } from 'react'
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import { revokeAnalyticsConsent, hasAnalyticsConsent } from '@/utils/analytics'
import SEOMetaTags from '@/components/SEOMetaTags'
import { getSEOConfig, getCanonicalUrl } from '@/utils/seo'

const PrivacyPolicy: React.FC = () => {
  // Get SEO configuration for this page
  const seoConfig = getSEOConfig('/privacy-policy')!
  const canonicalUrl = getCanonicalUrl('/privacy-policy')

  const [consentRevoked, setConsentRevoked] = useState(false)
  const hasConsent = hasAnalyticsConsent()

  const handleRevokeConsent = () => {
    revokeAnalyticsConsent()
    setConsentRevoked(true)
    // Reload page to stop tracking
    setTimeout(() => window.location.reload(), 2000)
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      {/* SEO Meta Tags */}
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={canonicalUrl}
        ogImage={seoConfig.ogImage}
        keywords={seoConfig.keywords}
        noIndex={seoConfig.noIndex}
      />

      <Typography
        variant="h3"
        component="h1"
        gutterBottom
        sx={{ fontWeight: 700, mb: 4 }}
      >
        Privacy Policy
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        <strong>Effective Date:</strong> November 23, 2025
        <br />
        <strong>Last Updated:</strong> November 23, 2025
      </Typography>

      {/* Introduction */}
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Introduction
        </Typography>
        <Typography variant="body1" paragraph>
          The National Council for Equal Shared Parenting (NCESP, we, us, or
          our) is committed to protecting your privacy. This Privacy Policy
          explains how we collect, use, disclose, and safeguard your information
          when you visit our website <strong>https://www.ncesp.org</strong> (the
          Site).
        </Typography>
        <Typography variant="body1">
          By using our Site, you consent to the data practices described in this
          policy. If you do not agree with this policy, please discontinue use
          of the Site.
        </Typography>
      </Paper>

      {/* Information We Collect */}
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Information We Collect
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
          1. Personal Information You Provide
        </Typography>
        <Typography variant="body1" paragraph>
          When you subscribe to our email list or contact us, we may collect:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="• Name (first and last)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Email address" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Phone number (optional)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• State of residence" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• ZIP code" />
          </ListItem>
        </List>

        <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
          2. Automatically Collected Information
        </Typography>
        <Typography variant="body1" paragraph>
          When you visit our Site, we automatically collect certain information
          about your device and usage patterns, including:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="• Browser type and version" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Operating system" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• IP address (anonymized)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Pages visited and time spent" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Referring website" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Click and navigation patterns" />
          </ListItem>
        </List>
      </Paper>

      {/* How We Use Your Information */}
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          How We Use Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We use the information we collect for the following purposes:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="• To send you updates about our advocacy efforts, research, and events"
              secondary="(with your consent via email subscription)"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="• To respond to your inquiries and requests"
              secondary="(when you contact us directly)"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="• To improve our website and user experience"
              secondary="(via analytics and usage data)"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="• To understand which content resonates with our audience"
              secondary="(via aggregated analytics)"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="• To comply with legal obligations"
              secondary="(if required by law)"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Google Analytics */}
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Google Analytics & Cookies
        </Typography>
        <Typography variant="body1" paragraph>
          We use <strong>Google Analytics 4</strong> to understand how visitors
          interact with our Site. Google Analytics collects information such as
          how often users visit our Site, what pages they visit, and what other
          sites they used prior to coming to our Site.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
          What Google Analytics Collects:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="• Page views and navigation paths" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Time spent on pages" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Device and browser information" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Geographic location (country/state level)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Referring websites" />
          </ListItem>
        </List>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          Google Analytics uses cookies (small text files stored on your device)
          to collect this information. We have configured Google Analytics to:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="• Anonymize IP addresses" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Set data retention to 2 months" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Disable advertising features and remarketing" />
          </ListItem>
        </List>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          For more information on how Google uses data, visit:{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Privacy Policy
          </a>
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Your Cookie Consent
        </Typography>
        {hasConsent && !consentRevoked ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              You have previously consented to analytics cookies.
            </Alert>
            <Button
              variant="outlined"
              color="error"
              onClick={handleRevokeConsent}
            >
              Revoke Analytics Consent
            </Button>
          </>
        ) : consentRevoked ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your analytics consent has been revoked. The page will reload to
            disable tracking.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have not consented to analytics cookies. Analytics are currently
            disabled.
          </Alert>
        )}
      </Paper>

      {/* Data Sharing */}
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          How We Share Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We do not sell, trade, or rent your personal information to third
          parties. We may share your information in the following limited
          circumstances:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="• Service Providers"
              secondary="We use Google Apps Script for email collection and Google Analytics for website analytics. These services have their own privacy policies."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="• Legal Requirements"
              secondary="We may disclose your information if required by law, court order, or government regulation."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="• Business Transfers"
              secondary="In the event of a merger or acquisition, your information may be transferred to the acquiring entity."
            />
          </ListItem>
        </List>
      </Paper>

      {/* Your Rights */}
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Your Privacy Rights
        </Typography>
        <Typography variant="body1" paragraph>
          Depending on your location, you may have the following rights:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="• Right to Access"
              secondary="Request a copy of the personal information we hold about you"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="• Right to Correction"
              secondary="Request correction of inaccurate information"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="• Right to Deletion"
              secondary="Request deletion of your personal information"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="• Right to Opt-Out"
              secondary="Unsubscribe from email communications or revoke analytics consent"
            />
          </ListItem>
        </List>
        <Typography variant="body1" sx={{ mt: 2 }}>
          To exercise these rights, please <a href="/contact">contact us</a>{' '}
          through our contact page.
        </Typography>
      </Paper>

      {/* Data Security */}
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Data Security
        </Typography>
        <Typography variant="body1" paragraph>
          We implement reasonable security measures to protect your personal
          information, including:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="• HTTPS encryption for data transmission" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Secure data storage via Google Apps Script" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Limited access to personal information" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Regular security reviews" />
          </ListItem>
        </List>
        <Typography variant="body1">
          However, no method of transmission over the Internet is 100% secure.
          We cannot guarantee absolute security of your information.
        </Typography>
      </Paper>

      {/* Children's Privacy */}
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Children&apos;s Privacy
        </Typography>
        <Typography variant="body1">
          Our Site is not intended for children under 13 years of age. We do not
          knowingly collect personal information from children under 13. If you
          believe we have collected information from a child under 13, please
          contact us immediately.
        </Typography>
      </Paper>

      {/* Changes to Policy */}
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Changes to This Privacy Policy
        </Typography>
        <Typography variant="body1">
          We may update this Privacy Policy from time to time. The updated
          version will be indicated by an updated Last Updated date. We
          encourage you to review this Privacy Policy periodically for any
          changes.
        </Typography>
      </Paper>

      {/* Contact Information */}
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have questions about this Privacy Policy or our data practices,
          please <a href="/contact">visit our contact page</a> to reach out to
          us.
        </Typography>
      </Paper>

      {/* Back to Home */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button variant="contained" href="/" size="large">
          Back to Home
        </Button>
      </Box>
    </Container>
  )
}

export default PrivacyPolicy
